import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { compose } from "redux";
import {
    CheckboxGroup,
    ControlLabel,
    FormControl,
    FormGroup,
    SelectPicker
} from "rsuite";
import { useIntl } from "react-intl";
import styled from "styled-components";

import FormHOC from "hoc/FilltersForm";
import CustomSelfClearedForm from "components/client/Form/CustomSelfClearedForm";
import { servicePick, toUTCDateTime } from "utils";
import { useWindowWidth } from "hooks";
import { FlexGrid, FlexGridItem } from "components/base/FlexGrid";
import m from "definedMessages";
import { BaseCheckboxField, BaseInputField } from "components/base/BaseForm";
import DateTimeRangePicker from "components/DateRangePicker/DateTimerRangePicker";
import { RtlContext } from "../../../App";

const EXCLUSIVE = new Set(["month", "day", "hour"]);


const TrafficStatFilters = ({
    mobile,
    filter,
    service,
    loading,
    onChangeFilters,
    filtersModel,
    subdestinationList,
    globalSettings = {},
    dateTimeState,
    setDateTimeState
}) => {
    const {formatMessage} = useIntl();
    const form = useRef(null);
    const resizedWidth = useWindowWidth();
    const rtl = useContext(RtlContext);

    // const todayDateStart = new Date(new Date().setHours(0, 0, 0, 0));
    // const todayDateEnd = new Date(new Date().setHours(23, 59, 59, 999));

    const groupsList = service ? [
        "range",
        "a_number",
        "b_number",
        "b_destination",
        "month"
    ] : [
        "range",
        "senderid",
        "phone",
        "destination",
        "month"
    ];

    const groupsString = service 
        ? "range/a_number/b_number/b_destination/month" 
        : "range/senderid/phone/destination/month";
    
    const defaultFilter = {
        range: dateTimeState,
        start_date: dateTimeState[0],
        end_date: dateTimeState[1],
        group: groupsString,
        ...(service ? {a_number: ""} : {senderid: ""})
    };

    const [formValue, setFormValue] = useState(filter || defaultFilter);

    const [checkboxState, setCheckboxState] = useState(filter?.checkbox);

    const parseDate = (value) => {
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    const getFilterRange = (currentFilter) => {
        if (Array.isArray(currentFilter?.range) && currentFilter.range.length === 2) {
            const start = parseDate(currentFilter.range[0]);
            const end = parseDate(currentFilter.range[1]);
            if (start && end) return [start, end];
        }

        if (Array.isArray(currentFilter?.start_end_date) && currentFilter.start_end_date.length === 2) {
            const start = parseDate(currentFilter.start_end_date[0]);
            const end = parseDate(currentFilter.start_end_date[1]);
            if (start && end) return [start, end];
        }

        if (currentFilter?.start_date && currentFilter?.end_date) {
            const start = parseDate(currentFilter.start_date);
            const end = parseDate(currentFilter.end_date);
            if (start && end) return [start, end];
        }

        return defaultFilter.range;
    };

    useEffect(() => {
        const initialRange = getFilterRange(filter);
        const initialCheckbox = Array.isArray(filter?.checkbox) && filter.checkbox.length
            ? replaceGroupByService(filter.checkbox, service)
            : replaceGroupByService(groupsList, service);

        const filtersData = {
            ...defaultFilter,
            ...filter,
            range: initialRange,
            group: initialCheckbox.join("/"),
            checkbox: initialCheckbox,
            start_date: initialRange[0],
            end_date: initialRange[1],
        };

        setDateTimeState(initialRange);
        setCheckboxState(initialCheckbox);
        setFormValue(filtersData);
        onChangeFilters(filtersData);
    }, [service]);

    const defaultFilterValue = useMemo(() => {
        return {
            ...filter,
            ...defaultFilter
        };
    }, [filter, service]);

    const replaceGroupByService = (checkboxList, service) => {
        const checkboxGroup = [...checkboxList];

        if (service === false) {
            checkboxGroup[checkboxGroup.indexOf("a_number")] = "senderid";
            checkboxGroup[checkboxGroup.indexOf("b_number")] = "phone";
        } else if (service === true) {
            checkboxGroup[checkboxGroup.indexOf("senderid")] = "a_number";
            checkboxGroup[checkboxGroup.indexOf("phone")] = "b_number";
        }

        return checkboxGroup;
    }

    useEffect(() => {
        const checkboxGroup = replaceGroupByService(groupsList, service);
        
        setCheckboxState(checkboxGroup);
    }, [service])

    const onSubmit = (data) => {
        const startDate = new Date(data.start_date);
        const endDate = new Date(data.end_date);

        if ( !form.current || !form.current.check() )
            return;

        // changeFormValue(formData);

        const filtersDataGroup = groupsList.reduce((sum, current) => (
            data["checkbox"] && data["checkbox"].find(dataItem => dataItem === current)
                ? [...sum, current]
                : sum
        ), []);

        const filtersData = {
            ...data,

            group: data["checkbox"]
                ? data["checkbox"].join("/")
                : checkboxState 
                    ? checkboxState.join("/")
                    : groupsString,

            checkbox: data["checkbox"]
                ? data["checkbox"]
                : checkboxState 
                    ? checkboxState
                    : groupsList,

            start_date: startDate,
            end_date: endDate
        };

        onChangeFilters(filtersData);
    };

    const groupItemList = service 
        ? [
            ["a_number", m.aNumber],
            ["b_number", m.bNumber],
            ["range", m.ranges],
            ["b_destination", m.destination],
            ["month", m.month],
            ["day", m.day],
            ["hour", m.hour]
        ]
        : [
            ["senderid", m.senderId],
            ["phone", m.bNumber],
            ["range", m.ranges],
            ["destination", m.destination],
            ["month", m.month],
            ["day", m.day],
            ["hour", m.hour]
        ];

    const handleChangeDateTime = (value) => {
        const filter = {...value};
        const currentDatetimeFilter = {range: [filter.range[0], filter.range[1]]};

        setDateTimeState(filter.range);

        const filtersToUpdate = {
            ...formValue,
            ...filter,
            ...currentDatetimeFilter
        };

        setFormValue(filtersToUpdate);

        const filterValues = {...filtersToUpdate};

        if (filterValues.range && filterValues.range.length) {
            // filterValues["start_date"] = toStartDayUTCTime(filterValues.range[0]);
            // filterValues["end_date"] = toEndDayUTCTime(filterValues.range[1]);
            filterValues["start_date"] = toUTCDateTime(filterValues.range[0]);
            filterValues["end_date"] = toUTCDateTime(filterValues.range[1]);

            // delete filterValues.range;
        }
        const checkedData = filtersModel.check(filterValues);
        onSubmit(filterValues, checkedData);
    };
    

    return (
        <CustomSelfClearedForm
            formRef={form}
            clearOn={service}
            model={filtersModel}
            onChange={(data) => {
                const formData = {
                    ...data,
                    range: formValue?.range
                };

                setFormValue(formData);

                if (formData.checkbox) {
                    delete formData.checkbox;
                }

                if (formData.group) {
                    delete formData.group;
                }

                const formDataValues = {
                    ...formData,
                    range: formValue?.range
                };

                if (formDataValues.range && formDataValues.range.length) {
                    // filterValues["start_date"] = toStartDayUTCTime(filterValues.range[0]);
                    // filterValues["end_date"] = toEndDayUTCTime(filterValues.range[1]);
                    formDataValues["start_date"] = toUTCDateTime(formDataValues.range[0]);
                    formDataValues["end_date"] = toUTCDateTime(formDataValues.range[1]);
        
                    // delete formDataValues.range;
                }

                onSubmit(formDataValues);
            }}
            formDefaultValue={defaultFilter}
            fluid
            disabled={loading}
        >
            <FlexGrid align="middle">
                {(resizedWidth > 406) &&
                    <FlexGridItem>{formatMessage(m.dateFrom)}</FlexGridItem>
                }
                <FreeSpaceFlexGridItem>
                    <DateTimeRangePicker
                        onChange={(value)=> {
                            handleChangeDateTime(value);
                        }}

                        defaultStartDate={dateTimeState[0]}
                        defaultEndDate={dateTimeState[1]}
                        startDate={formValue?.range ? formValue?.range[0] : defaultFilter.range[0]}
                        endDate={formValue?.range ? formValue?.range[1] : defaultFilter.range[1]}
                        mobile={mobile}
                        // input={false}
                    />
                </FreeSpaceFlexGridItem>

                <FlexGridItem>
                    <FormControl
                        name={servicePick(service, "b_sde_key", "phone_sde_key")}
                        accepter={SelectPicker}
                        data={subdestinationList}
                        valueKey="sde_key"
                        labelKey="name"
                        style={{width: 260}}
                        placement={resizedWidth >= 768 ? (rtl ? "bottomEnd" : "bottomStart") : "auto"}
                        placeholder={formatMessage(m.destination)}
                    />
                </FlexGridItem>

                <FlexGridItem>
                    <FormControl
                        name={servicePick(service, "a_number", "senderid")}
                        accepter={BaseInputField}
                        placement={resizedWidth >= 768 ? (rtl ? "bottomEnd" : "bottomStart") : "auto"}
                        placeholder={servicePick(service, formatMessage(m.aNumber), formatMessage(m.senderId))}
                    />
                </FlexGridItem>

                <FlexGridItem>
                    <FormControl
                        name={servicePick(service, "b_number", "phone")}
                        accepter={BaseInputField}
                        placement={resizedWidth >= 768 ? (rtl ? "bottomEnd" : "bottomStart") : "auto"}
                        placeholder={servicePick(service, formatMessage(m.bNumber), formatMessage(m.prefix))}
                    />
                </FlexGridItem>
            </FlexGrid>
            <GroupingFlexGrid>
                {resizedWidth > 406 &&
                    <FlexGridItem>
                        <ControlLabel>{formatMessage(m.groupBy)}</ControlLabel>
                    </FlexGridItem>
                }

                <FlexGridItem>
                    <FormGroup>
                        <FormControl
                            name="checkbox"
                            accepter={CheckboxGroup}
                            inline
                            value={checkboxState}
                            onChange={(value) => {
                                const prev = checkboxState ?? [];

                                const added = value.filter(v => !prev.includes(v));
                                const addedExclusive = added.find(v => EXCLUSIVE.has(v));

                                let result = value.slice();

                                if (addedExclusive) {
                                    result = result.filter(v => !EXCLUSIVE.has(v) || v === addedExclusive);
                                } else {
                                    const exclusives = result.filter(v => EXCLUSIVE.has(v));
                                    if (exclusives.length > 1) {
                                        const keep = exclusives[exclusives.length - 1];
                                        result = result.filter(v => !EXCLUSIVE.has(v) || v === keep);
                                    }
                                }

                                setCheckboxState(result);

                                const filterData = {
                                    ...filter,
                                    checkbox: result
                                };

                                onSubmit(filterData);
                            }}
                        >
                            {groupItemList.map(item => {
                                const key = item[0];
                                const message = (typeof item[1]) === "string" ? item[1] : formatMessage(item[1]);

                                return (<BaseCheckboxField
                                    key={key}
                                    value={key}
                                    disabled={loading}
                                >
                                    {message}
                                </BaseCheckboxField>)
                            })}
                        </FormControl>
                    </FormGroup>
                </FlexGridItem>
            </GroupingFlexGrid>
        </CustomSelfClearedForm>
    );
};

export default compose(
    FormHOC,
)(TrafficStatFilters);


const FreeSpaceFlexGridItem = styled(FlexGridItem)`
    && {
        width: auto;
        position: inherit;
    }
`;

const GroupingFlexGrid = styled(FlexGrid)`
    && {
        margin-top: 20px;
    }
`;